import React from "react";
import { useNavigate } from "react-router-dom";
import StatusPill from "./StatusPill";
import { getLeagueStatus, previewStatus } from "./leagueStatus";
import { formatPrize, previewTagline } from "./leagueFormat";
import { PrizeTrophyIcon } from "./icons";
import useMinuteTick from "./useMinuteTick";
import { competitionDestination } from "../../../utils/leaguePath";

// One cell of the Entry / Starts In row. The label is the same quiet 9px
// uppercase grey in both of the client's files; only the figure changes colour.
const Stat = ({ label, value, valueClass }) => (
  <div className="flex flex-col items-center">
    <span className="text-[9px] font-bold uppercase tracking-widest text-tertiary">
      {label}
    </span>
    <span className={`mt-1 font-extrabold text-[14px] leading-none ${valueClass}`}>
      {value}
    </span>
  </div>
);

// The headline cash game on League Play home.
//
// Built to the client's own two Stitch files for this card (mobile and desktop,
// "i have focused on what the Starting soon hero will look like"). Centred
// stack throughout: status → market/risk line → title → hook → prize pool →
// entry/countdown → CTA. Deliberately no league artwork — the prize is the
// picture.
//
// Where the two files differ they differ only in scale, and the desktop values
// are applied at md:
//
//   card        rounded-[28px] / rounded-[2rem], p-5 / p-6
//   title       20px extrabold / 30px
//   tagline     11px / 18px
//   prize       full width, 20px figure / three-fifths wide, 30px figure
//   CTA         full width / a button's own width
//
// The one value taken from the mobile file at BOTH widths is the top rule's
// green (#1D9E75). The desktop file paints it #35B23C at 50% opacity, which is
// the same stripe two shades apart — a colour that changes at a breakpoint
// would be a bug, not a design, so the tighter file wins as the client said it
// should.
const FeaturedCashHeroCard = ({ game }) => {
  const navigate = useNavigate();
  const status = getLeagueStatus(game);
  const isLive = status?.tone === "live";
  const isEnded = status?.tone === "ended";
  const ctaLabel = isLive ? "View Leaderboard" : isEnded ? "View Results" : "View Game";
  const prize = formatPrize(game?.cash_prize_amount);
  // Client: "The League Play home cash game hero card - the description is
  // showing up where the tagline should be. The line beneath the headline must
  // pull the Cash Game tagline, not the description."
  //
  // previewTagline is the same reader CashGameRow and the league previews use,
  // so the hook here and the second line on every other card come from one
  // field. The tagline is capped at 36 characters by the wizard, so there is
  // nothing to truncate — the old shortDescription(…, 10) was only ever there
  // to cut a long description down to a hook.
  const hook = previewTagline(game);

  // Keep the countdown honest while the page sits open.
  useMinuteTick(!!status?.countdownValue);

  return (
    <div className="relative rounded-[28px] bg-white border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col md:rounded-[2rem]">
      {/* The featured card's marker: a hairline of green across the top edge,
          rather than a coloured panel that would fight the prize block. */}
      <div className="h-1 bg-[#1D9E75]" />

      <div className="px-5 pt-3 pb-3 md:px-6 md:pt-4 md:pb-6 flex flex-col flex-1 text-center">
        <div className="mx-auto w-full max-w-2xl flex flex-col flex-1">
          {status && (
            <div className="flex justify-center">
              {/* Text-only pill: the countdown below already carries the clock.
                  `variant="cash"` paints the client's Cash Game palette — gold
                  #D7C588 behind dark teal for Starting Soon, neutral grey behind
                  dark teal for Upcoming — without touching the shared pill
                  colours every other league preview uses. */}
              <StatusPill status={status} withIcon={false} variant="cash" />
            </div>
          )}

          {/* Client: "The LIVE MARKETS ZERO RISK should use the same grey you use
              in the club challenge eyebrows." That eyebrow is `text-secondary`
              (#6B7280) — see ClubChallengeCard — so this is the same token rather
              than a new grey that only looks close. */}
          <p className="mt-2 text-[9px] md:text-[11px] uppercase tracking-widest font-bold text-secondary">
            Live markets • Zero risk
          </p>

          <h3 className="mt-1 font-montserrat font-extrabold text-xl md:text-3xl leading-tight tracking-tight text-[#0F172A]">
            {game?.title}
          </h3>

          <p className="mt-1 md:mt-2 text-[11px] md:text-lg font-medium text-secondary">
            {hook || "Trade the open. Own the week."}
          </p>

          {prize && (
            /* Client: "PRIZE POOL and the trophy are our #006565; the prize bar
                fill is light grey, not green." Both files draw it as a plain
                grey block with no border — full width on the phone, three
                fifths of the card and centred on desktop. The figure itself
                stays near-black: it is the loudest thing on the card and both
                of the client's files keep it that way. */
            <div className="mt-3 w-full md:mt-4 md:w-3/5 md:mx-auto rounded-xl bg-[#F3F4F6] px-3 py-2.5 flex items-center justify-between text-left">
              <div>
                <span className="text-[9px] uppercase tracking-wider font-bold text-[#006565]">
                  Prize pool
                </span>
                <div className="mt-0.5 font-extrabold text-xl md:text-3xl leading-none text-[#0F172A]">
                  {prize}
                </div>
              </div>
              <PrizeTrophyIcon className="w-6 h-6 shrink-0" fill="#006565" />
            </div>
          )}

          <div className="mt-2 w-full md:w-3/5 md:mx-auto grid grid-cols-2 items-center py-2 border-y border-gray-100 md:border-b-0">
            <div className="border-r border-gray-100">
              <Stat label="Entry" value="Free" valueClass="text-[#35B23C]" />
            </div>
            {/* Client: "The Starts in utilizes the same countdown we use in league
                play (2h, not 2h 15m. Under an hour, it goes to 59m) in the same
                starting soon status token color #006565."
                The countdown format already reads in one unit — see
                leagueStatus.formatCountdown.

                And then: "League Play Home featured cash game still rendering
                ENDS IN time in the wrong token color." It was: this cell printed
                #006565 for EVERY tone, so a live game's ENDS IN wore the Starts
                in colour. The four tokens live in previewStatus (Starts in
                #006565, Ends in #35B23C, Ended grey), so the colour is read from
                there instead of being restated — this card can no longer pick
                its own. */}
            <Stat
              label={isLive ? "Ends in" : "Starts in"}
              value={status?.countdownValue || "—"}
              valueClass={previewStatus(status, game)?.cls || "text-[#006565]"}
            />
          </div>

          {/* Full-width on mobile; on desktop it keeps a button's own width and
              centres under the stack. */}
          {/* Client: "(Live now) Live state CTA: 'View Leaderboard,' not View
              Live. Once a game starts, no one should be able to enter the game
              other than members."

              Two faults in one button. The label said "View Live", which is not
              one of the three states the client has named, and BOTH labels went
              to the same place — /contest/<slug>, the landing page whose whole
              purpose is the Enter Free CTA. So the live card was still walking
              non-members to a join screen for a competition they can no longer
              enter.

              It now matches the other cash cards exactly: live opens the
              leaderboard on its own page, everything else opens the game. */}
          <button
            type="button"
            onClick={() =>
              navigate(competitionDestination(game, status))
            }
            className="mt-3 md:mt-5 w-full md:w-auto md:self-center md:px-12 md:py-4 rounded-xl bg-[#35B23C] hover:bg-[#2E9A34] text-white py-3.5 text-sm font-extrabold shadow-md transition-colors"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedCashHeroCard;
